'use client';

import { useEffect, useMemo, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import {
  AlignCenterIcon,
  AlignLeftIcon,
  AlignRightIcon,
  BoldIcon,
  ItalicIcon,
  ListIcon,
  ListOrderedIcon,
  UnderlineIcon,
} from 'lucide-react';
import sanitizeHtml, { IOptions as SanitizeOptions } from 'sanitize-html';
import clsx from 'clsx';

// swap with your cn() if you have one
const cn = (...a: any[]) => clsx(...a);

export type TiptapInputProps = {
  value: string;
  onChange: (html: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string; // outer wrapper (border, bg)
  contentClassName?: string; // extra classes for the editable area`
  sanitizeOnChange?: boolean;
  sanitizeOptions?: SanitizeOptions;
};

export default function TiptapInput({
  value,
  onChange,
  onBlur,
  disabled,
  placeholder = 'Type here...',
  className,
  contentClassName,
  sanitizeOnChange = false,
  sanitizeOptions,
}: TiptapInputProps) {
  const sanitize = useMemo(() => {
    if (!sanitizeOnChange) return (html: string) => html;
    const opts: SanitizeOptions =
      sanitizeOptions ??
      ({
        allowedTags: [
          'b',
          'i',
          'em',
          'strong',
          'a',
          'p',
          'br',
          'ul',
          'ol',
          'li',
          'h1',
          'h2',
          'h3',
          'h4',
          'h5',
          'h6',
          'div',
          'span',
        ],
        allowedAttributes: {
          'a': ['href', 'target', 'rel'],
          '*': ['class', 'style'],
        },
      } as SanitizeOptions);
    return (html: string) => sanitizeHtml(html, opts);
  }, [sanitizeOnChange, sanitizeOptions]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: { keepMarks: true },
        orderedList: { keepMarks: true },
      }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    editable: !disabled,
    onUpdate: ({ editor }) => onChange(sanitize(editor.getHTML())),
    onBlur: () => onBlur?.(),
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  // Keep editor synced with external value (e.g., form reset)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== current) editor.commands.setContent(value || '');
  }, [value, editor]);

  // Track focus state for outer focus ring
  const [isFocused, setIsFocused] = useState(false);

  // Re-render toolbar when state changes so isActive() updates
  const [, tick] = useState(0);
  useEffect(() => {
    if (!editor) return;
    const update = () => tick((x) => x + 1);
    const handleFocus = () => {
      setIsFocused(true);
      update();
    };
    const handleBlur = () => {
      setIsFocused(false);
      update();
    };
    editor.on('selectionUpdate', update);
    editor.on('transaction', update);
    editor.on('update', update);
    editor.on('focus', handleFocus);
    editor.on('blur', handleBlur);
    return () => {
      editor.off('selectionUpdate', update);
      editor.off('transaction', update);
      editor.off('update', update);
      editor.off('focus', handleFocus);
      editor.off('blur', handleBlur);
    };
  }, [editor]);

  if (!editor) {
    return (
      <div
        className={cn(
          'rounded-md border border-zinc-300 bg-white p-3 text-sm text-zinc-500',
          className,
        )}
      >
        Loading editor…
      </div>
    );
  }

  const baseBtn =
    'rounded-sm p-1 hover:bg-zinc-200 disabled:opacity-40 disabled:cursor-not-allowed';
  const activeBtn = 'bg-zinc-700 text-white hover:bg-zinc-700';

  const isAlignActive = (align: 'left' | 'center' | 'right' | 'justify') =>
    editor.isActive({ textAlign: align }) ||
    editor.isActive('paragraph', { textAlign: align }) ||
    editor.isActive('heading', { textAlign: align });

  const setAlign = (align: 'left' | 'center' | 'right' | 'justify') => {
    if (editor.commands?.setTextAlign)
      return editor.chain().focus().setTextAlign(align).run();
    editor
      .chain()
      .focus()
      .updateAttributes('paragraph', { textAlign: align })
      .updateAttributes('heading', { textAlign: align })
      .run();
  };

  const handleContentAreaClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // If clicking on the wrapper div (not directly on editor content), focus the editor
    const target = e.target as HTMLElement;
    const proseMirror = target.closest('.ProseMirror');

    // If we clicked outside the ProseMirror element, focus the editor
    if (!proseMirror || target === e.currentTarget) {
      e.preventDefault();
      editor.commands.focus();
    }
  };

  return (
    <div
      className={cn(
        'rounded-lg border bg-white transition-colors',
        isFocused ? 'border-ring ring-ring/50 ring-[3px]' : 'border-zinc-200',
        className,
      )}
    >
      {/* Toolbar */}
      <div className="flex items-center gap-2 border-b border-zinc-200 px-2 py-1">
        <button
          type="button"
          className={cn(baseBtn, editor.isActive('bold') && activeBtn)}
          onClick={() => editor.chain().focus().toggleMark('bold').run()}
          disabled={!editor.can().chain().focus().toggleMark('bold').run()}
          aria-label="Bold"
        >
          <BoldIcon size={18} />
        </button>
        <button
          type="button"
          className={cn(baseBtn, editor.isActive('italic') && activeBtn)}
          onClick={() => editor.chain().focus().toggleMark('italic').run()}
          disabled={!editor.can().chain().focus().toggleMark('italic').run()}
          aria-label="Italic"
        >
          <ItalicIcon size={18} />
        </button>
        <button
          type="button"
          className={cn(baseBtn, editor.isActive('underline') && activeBtn)}
          onClick={() => editor.chain().focus().toggleMark('underline').run()}
          disabled={!editor.can().chain().focus().toggleMark('underline').run()}
          aria-label="Underline"
        >
          <UnderlineIcon size={18} />
        </button>

        <span className="mx-1 h-5 w-px bg-zinc-200" />

        <button
          type="button"
          className={cn(baseBtn, isAlignActive('left') && activeBtn)}
          onClick={() => setAlign('left')}
          aria-label="Align left"
        >
          <AlignLeftIcon size={18} />
        </button>
        <button
          type="button"
          className={cn(baseBtn, isAlignActive('center') && activeBtn)}
          onClick={() => setAlign('center')}
          aria-label="Align center"
        >
          <AlignCenterIcon size={18} />
        </button>
        <button
          type="button"
          className={cn(baseBtn, isAlignActive('right') && activeBtn)}
          onClick={() => setAlign('right')}
          aria-label="Align right"
        >
          <AlignRightIcon size={18} />
        </button>

        <span className="mx-1 h-5 w-px bg-zinc-200" />

        <button
          type="button"
          className={cn(baseBtn, editor.isActive('orderedList') && activeBtn)}
          onClick={() =>
            editor.chain().focus().toggleList('orderedList', 'listItem').run()
          }
          disabled={
            !editor
              .can()
              .chain()
              .focus()
              .toggleList('orderedList', 'listItem')
              .run()
          }
          aria-label="Ordered list"
        >
          <ListOrderedIcon size={18} />
        </button>
        <button
          type="button"
          className={cn(baseBtn, editor.isActive('bulletList') && activeBtn)}
          onClick={() =>
            editor.chain().focus().toggleList('bulletList', 'listItem').run()
          }
          disabled={
            !editor
              .can()
              .chain()
              .focus()
              .toggleList('bulletList', 'listItem')
              .run()
          }
          aria-label="Bullet list"
        >
          <ListIcon size={18} />
        </button>
      </div>

      <div
        className="relative h-60 grow cursor-text"
        onClick={handleContentAreaClick}
        onMouseDown={(e) => {
          // If clicking in the margin area (not on ProseMirror), focus the editor
          const target = e.target as HTMLElement;
          const proseMirror = target.closest('.ProseMirror');

          if (!proseMirror || target === e.currentTarget) {
            e.preventDefault();
            // Small delay to ensure the click doesn't interfere with editor's own handling
            setTimeout(() => {
              editor.commands.focus();
            }, 0);
          }
        }}
      >
        {/* Editable area — apply `prose` directly here */}
        <EditorContent
          editor={editor}
          onBlur={onBlur}
          className={cn(
            'prose prose-sm flex h-full max-w-none flex-col overflow-auto p-2',
            // Ensure ProseMirror fills the container
            '[&_.ProseMirror]:min-h-full [&_.ProseMirror]:w-full',
            // Remove focus styles from all editor elements
            '[&_.ProseMirror]:outline-none [&_.ProseMirror]:focus:outline-none [&_.ProseMirror]:focus-visible:outline-none',
            '[&_.ProseMirror_*]:outline-none [&_.ProseMirror_*]:focus:outline-none [&_.ProseMirror_*]:focus-visible:outline-none',
            '[&_.ProseMirror_*]:ring-0 [&_.ProseMirror_*]:focus:ring-0 [&_.ProseMirror_*]:focus-visible:ring-0',
            contentClassName,
          )}
        />
      </div>
    </div>
  );
}
